import { AxiosError } from 'axios';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { MutateOptions, UseMutateFunction, useMutation, useQuery, useQueryClient } from 'react-query';

import { datadogRum } from '@datadog/browser-rum';
import {
  getAnalyticsContextDefaultProperties,
  identify,
  reset as resetTracking,
  useTranslation,
} from '@helios/frontend-core';
import {
  clearCredentialStorage,
  isImpersonatedUser,
  removeImpersonationCookie,
  stopImpersonating,
} from '@helios/frontend-auth';

import { PalmettoToaster } from '@palmetto/dsm-components';
import authContext from '../../components/_app/AuthProvider/authContext';

import { shutdownIntercom } from '../../components/IntercomBubble/IntercomBubble';
import { useDebug } from '@helios/frontend-project';
import { LOGIN_REDIRECT_KEY } from '../../pages/Lander/useAuthRedirect';
import { getFriendlyError, getFriendlySignUpError } from '../../utils/error';
import {
  checkUserExists,
  CheckUserExistsResponse,
  LoginUser,
  LoginUserResponse,
  postResetPassword,
  signup,
  SignupUser,
} from '../login/login';
import { ApiUser, UpdateUser, User, UserSegmentation, UserSettings } from '../user/types';
import { getUser, putUser } from '../user/user';

export interface UseUser {
  login: UseMutateFunction<LoginUserResponse, AxiosError, LoginUser>;
  logout: () => void;
  isAuthenticated: boolean;
  user: User | undefined;
  updateUser: UseMutateFunction<User, AxiosError, UpdateUser>;
  updateUserSettings: (
    settings: UserSettings,
    options?: MutateOptions<ApiUser, AxiosError, UpdateUser, unknown>,
  ) => void;
  updateUserSegmentation: UseMutateFunction<
    User,
    AxiosError,
    { settings?: UserSettings; segmentation?: UserSegmentation }
  >;
  resetPassword: UseMutateFunction<void, AxiosError, string>;
  signupUser: UseMutateFunction<{ redirectUrl?: string }, AxiosError, SignupUser>;
  checkUserExists: (token: string) => Promise<CheckUserExistsResponse>;
  isImpersonatedUser: () => boolean;
  stopImpersonating: () => void;
  isLoading: boolean;
  isFetched: boolean;
  isIdentifying: boolean;
  isLaunchDarklyIdentified: boolean;
  error?: string;
  invalidateQueries: () => void;
  setIsAuthenticated: Dispatch<SetStateAction<boolean>>;
  isAuthLoading: boolean;
  refetch: () => void;
}

export const QUERY_KEY = 'user';

let ldIdentifiedUser: User | undefined;
let ldIdentifiedAuth0Id: string | undefined;
const ldIdentifiedListeners = new Set<() => void>();

const setLdIdentifiedAuth0Id = (auth0Id: string) => {
  ldIdentifiedAuth0Id = auth0Id;
  ldIdentifiedListeners.forEach((listener) => listener());
};

const subscribeToLdIdentified = (listener: () => void) => {
  ldIdentifiedListeners.add(listener);
  return () => {
    ldIdentifiedListeners.delete(listener);
  };
};

const getLdIdentifiedAuth0Id = () => ldIdentifiedAuth0Id;

const identifyUser = async (user: User, identifyWithAnalytics: boolean) => {
  if (!identifyWithAnalytics) return;
  try {
    await identify(
      user.auth0Id,
      {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        locale: user.languageCode,
        propertyIds: user.propertyIds,
      },
      {
        ...user,
        context: await getAnalyticsContextDefaultProperties(),
      },
    );
  } catch (e) {
    datadogRum.addError('Error identifying user for analytics:', e.message);
  }
};

export const useUser = (params?: { refetchInterval?: number }): UseUser => {
  const launchDarkly = useLDClient();
  const { refetchInterval } = params || {};
  const { login, logout, isAuthenticated, isAuthLoading, loginError, setIsAuthenticated } = useContext(authContext);
  const queryClient = useQueryClient();
  const debug = useDebug();
  const [isIdentifying, setIsIdentifying] = useState(false);
  const identifiedAuth0Id = useSyncExternalStore(subscribeToLdIdentified, getLdIdentifiedAuth0Id);
  const { t } = useTranslation();

  const invalidateQueries = () => {
    queryClient.invalidateQueries(QUERY_KEY);
  };

  const {
    data: user,
    error: userError,
    isLoading: isUserLoading,
    isFetched,
    refetch,
  } = useQuery<User | undefined, AxiosError>(
    [QUERY_KEY, isAuthenticated],
    async () => {
      try {
        const u = await getUser();
        debug('useUser getUser', { user: u });
        if (u?.auth0Id) {
          try {
            await identifyUser(u, !isImpersonatedUser());
          } catch (error) {
            console.error('Error identifying user:', error);
          }
        }
        return u;
      } catch (e) {
        if (e.code === 'ECONNABORTED') {
          return;
        }

        if (e.response?.status === 401) {
          PalmettoToaster.error({ title: t('common.toasts.unauthorizedUser') });
        } else if (isImpersonatedUser() && e.response?.status === 403) {
          PalmettoToaster.error({ title: t('common.toasts.impersonationError') });
          removeImpersonationCookie();
        } else {
          PalmettoToaster.error({ title: t('common.toasts.errorLoadingUser') });
        }

        logoutUser();
        throw e;
      }
    },
    {
      enabled: isAuthenticated,
      retry: false,
      refetchOnWindowFocus: true,
      refetchInterval,
    },
  );

  useEffect(() => {
    if (!user?.auth0Id || ldIdentifiedUser === user) return;
    if (!launchDarkly) {
      datadogRum.addError('Failed to identify user for LaunchDarkly: launchDarkly is undefined');
      return;
    }
    ldIdentifiedUser = user;
    setIsIdentifying(true);
    const propertyKey = user.propertyIds?.[0];
    const firstProperty = user.properties?.[0];
    launchDarkly
      .identify({
        kind: 'multi',
        user: {
          ...user.segmentation,
          key: user.auth0Id,
          email: user.email,
          name: `${user.firstName} ${user.lastName}`,
          firstName: user.firstName,
          lastName: user.lastName,
          anonymous: false,
        },
        ...(propertyKey
          ? {
              property: {
                key: propertyKey,
                ...(firstProperty?.stateCode ? { state: firstProperty.stateCode } : {}),
                ...(firstProperty?.city ? { city: firstProperty.city } : {}),
                ...(firstProperty?.zip ? { postalCode: firstProperty.zip } : {}),
                ...(user.lseId ? { lseId: user.lseId } : {}),
                ...(user.eiaId ? { eiaId: user.eiaId } : {}),
              },
            }
          : {}),
      })
      .catch((e) => datadogRum.addError('Error identifying user for LaunchDarkly:', e?.message))
      .finally(() => {
        setIsIdentifying(false);
        setLdIdentifiedAuth0Id(user.auth0Id);
      });
  }, [user, launchDarkly]);

  /*
   If starting polling, then clear the query cache
   If ending polling, do not clear the query cache
   */
  const previousRefetchInterval = useRef<number>();
  useEffect(() => {
    if (previousRefetchInterval.current !== refetchInterval) {
      previousRefetchInterval.current = refetchInterval;
      if (refetchInterval) {
        invalidateQueries();
      }
    }
  }, [refetchInterval, queryClient]);

  const {
    mutate: updateUser,
    error: updateUserError,
    isLoading: isUpdateUserLoading,
  } = useMutation<User, AxiosError, UpdateUser>(putUser, {
    onMutate: (u) => Boolean(u.email) && clearCredentialStorage(),
    onSuccess: invalidateQueries,
  });

  const updateUserSettings = useCallback(
    (settings: UserSettings, options: MutateOptions<ApiUser, AxiosError, UpdateUser, unknown> | undefined) =>
      updateUser({ settings }, options),
    [updateUser],
  );

  const updateUserSegmentation = useCallback(
    (
      args: { settings?: UserSettings; segmentation?: UserSegmentation },
      options?: MutateOptions<User, AxiosError, { settings?: UserSettings; segmentation?: UserSegmentation }>,
    ) => updateUser(args, options),
    [updateUser],
  );

  const {
    mutate: signupUser,
    error: signupUserError,
    isLoading: isSignupUserLoading,
  } = useMutation<{ redirectUrl?: string }, AxiosError, SignupUser>(signup, {
    onSuccess: ({ redirectUrl }) => {
      if (redirectUrl) {
        window.localStorage.setItem(LOGIN_REDIRECT_KEY, redirectUrl);
      }
      setIsAuthenticated(true);
    },
  });

  const {
    mutate: resetPassword,
    isLoading: isResetPasswordLoading,
    error: resetPasswordError,
  } = useMutation<void, AxiosError, string>(postResetPassword, {
    onMutate: clearCredentialStorage,
  });

  const logoutUser = async () => {
    shutdownIntercom();
    await resetTracking();
    logout();
  };

  return {
    updateUser,
    updateUserSettings,
    updateUserSegmentation,
    refetch,
    isAuthenticated,
    user,
    login,
    logout: logoutUser,
    resetPassword,
    signupUser,
    checkUserExists,
    isImpersonatedUser,
    stopImpersonating,
    isLoading:
      isUpdateUserLoading || isUserLoading || isResetPasswordLoading || isSignupUserLoading || isAuthLoading,
    isIdentifying,
    isLaunchDarklyIdentified: !launchDarkly || !user?.auth0Id || identifiedAuth0Id === user.auth0Id,
    isFetched,
    invalidateQueries: () => queryClient.invalidateQueries(QUERY_KEY),
    error:
      loginError ||
      getFriendlySignUpError(signupUserError) ||
      getFriendlyError(updateUserError || userError || resetPasswordError, 'user'),
    setIsAuthenticated,
    isAuthLoading,
  };
};
